import { useEffect, useEffectEvent, useMemo, useRef, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";

import type { components } from "../../api/schema";
import { firstErrorMessage } from "../../lib/errors";
import { reviewSelection, type ReviewAnswer } from "../../lib/reviewFeed";
import { Button, Inline, Modal, Stack, Text } from "../../ui";
import { ReviewFeedBand } from "./ReviewFeedBand";

type Answer = components["schemas"]["AnswerResponse"];

export function useReviewContinuation({
  answer,
  feed,
  isDirty,
  isSaving,
  onOpen,
  onReturn,
}: {
  answer: Answer | null;
  feed?: string;
  isDirty: boolean;
  isSaving: boolean;
  onOpen: (answer: ReviewAnswer, afterSave?: boolean) => void;
  onReturn?: () => void;
}) {
  const client = useQueryClient();
  const selection = useMemo(
    () => (feed ? reviewSelection(client, feed) : undefined),
    [client, feed],
  );
  const [state, setState] = useState({
    ended: false,
    error: null as string | null,
    pending: false,
    readyId: "",
  });
  const [leave, setLeave] = useState<(-1 | 1) | null>(null);
  const busy = useRef(false);
  const retry = useRef<() => void>(() => undefined);
  const latestId = useRef(answer?.id);
  const latestSelection = useRef(selection);
  const ready = !selection || state.readyId === answer?.id;
  const onEntry = useEffectEvent((next: ReviewAnswer | null) => {
    if (next && next.id !== answer?.id) {
      onOpen(next);
    }
  });

  useEffect(() => {
    latestId.current = answer?.id;
    latestSelection.current = selection;
    if (!selection || !answer) {
      return;
    }
    let active = true;
    const check = async () => {
      try {
        const next = await selection.entry(answer);
        if (!active) {
          return;
        }
        setState({
          ended: !next,
          error: null,
          pending: false,
          readyId: !next || next.id === answer.id ? answer.id : "",
        });
        onEntry(next);
        if (next?.id === answer.id) {
          // Speculative read errors do not block the current Label. Navigation
          // repeats eligibility and reports any error with an explicit Retry.
          void selection.prefetchNext(answer.id).catch(() => undefined);
        }
      } catch (error) {
        if (active) {
          setState({
            ended: false,
            pending: false,
            readyId: "",
            error:
              firstErrorMessage(error) ??
              "Could not check this selection. Retry to continue.",
          });
        }
      }
    };
    retry.current = () => {
      void check();
    };
    void check();
    return () => {
      active = false;
      latestId.current = undefined;
    };
  }, [answer, selection]);

  async function move(direction: -1 | 1, afterSave = false): Promise<void> {
    if (!selection || !answer || busy.current || (isSaving && !afterSave)) {
      return;
    }
    busy.current = true;
    setState((current) => ({ ...current, error: null, pending: true }));
    retry.current = () => {
      void move(direction, afterSave);
    };
    try {
      const next = await selection.adjacent(answer.id, direction);
      if (
        latestId.current !== answer.id ||
        latestSelection.current !== selection
      ) {
        return;
      }
      if (next) {
        onOpen(next, afterSave);
      }
      setState((current) => ({ ...current, ended: !next && direction === 1 }));
    } catch (error) {
      setState((current) => ({
        ...current,
        error:
          firstErrorMessage(error) ??
          "Could not open the next transcript. Retry to continue.",
      }));
    } finally {
      busy.current = false;
      setState((current) => ({ ...current, pending: false }));
    }
  }

  function requestMove(direction: -1 | 1): void {
    if (isSaving || state.pending || !ready) {
      return;
    }
    if (isDirty) {
      setLeave(direction);
    } else {
      void move(direction);
    }
  }

  const position =
    !ready || !answer ? "…" : (selection?.position(answer.id) ?? "");

  return {
    ended: ready && state.ended,
    isPending: Boolean(selection) && (!ready || state.pending),
    move,
    next: () => {
      requestMove(1);
    },
    previous: () => {
      requestMove(-1);
    },
    selection,
    band: selection ? (
      <>
        <ReviewFeedBand
          ended={ready && state.ended}
          error={state.error}
          hasPrevious={answer != null && selection.hasPrevious(answer.id)}
          isPending={state.pending || isSaving || (!ready && !state.error)}
          labelsSaved={selection.labelsSaved}
          onNext={() => {
            requestMove(1);
          }}
          onPrevious={() => {
            requestMove(-1);
          }}
          onReturn={onReturn}
          onRetry={() => {
            retry.current();
          }}
          position={position}
        />
        <Modal
          ariaLabel="Discard unsaved Label changes?"
          isOpen={leave !== null}
          onDismiss={() => {
            setLeave(null);
          }}
        >
          <Stack gap={4}>
            <Text>This Label has unsaved changes. Leaving discards them.</Text>
            <Inline>
              <Button
                onClick={() => {
                  setLeave(null);
                }}
              >
                Keep editing
              </Button>
              <Button
                variant="danger"
                onClick={() => {
                  const direction = leave;
                  setLeave(null);
                  if (direction) {
                    // The user explicitly discarded this draft; allow the
                    // resulting route navigation through the route guard.
                    void move(direction, true);
                  }
                }}
              >
                Discard changes
              </Button>
            </Inline>
          </Stack>
        </Modal>
      </>
    ) : null,
  };
}
